import * as Sentry from '@sentry/react';

import { REACT_APP_BE_API_URL } from '#shared';

import { handleSessionExpired } from '../auth';

import { fetchWithAuthHeaders } from './fetchWithAuthHeaders';

import type { BaseQueryFn } from '@reduxjs/toolkit/query';

const FETCH_TIMEOUT = 100_000; // 100 seconds

export const fetchWithAuthBaseQuery: BaseQueryFn<
  string,
  unknown,
  { error: string },
  unknown,
  { signal?: AbortSignal }
> = async (url, api) => {
  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(
    () => controller.abort(),
    FETCH_TIMEOUT,
  );

  // Link to RTK Query's abort signal (for component unmount/cache invalidation)
  api.signal?.addEventListener('abort', () => controller.abort());

  try {
    const fullUrl = `${REACT_APP_BE_API_URL}${url}`;
    const data = await fetchWithAuthHeaders(fullUrl, true, {
      signal: controller.signal,
    });
    return { data };
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { error: { error: 'Request timed out' } };
    }
    // Recover a lost auth session (loop-guarded) instead of freezing.
    // handleSessionExpired never rejects, but guard the fire-and-forget so a
    // future change can't leak an unhandled rejection.
    void handleSessionExpired(err).catch(handlerError =>
      Sentry.captureException(handlerError),
    );
    return {
      error: { error: err instanceof Error ? err.message : 'Unknown error' },
    };
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
};
